import React, {useState, useEffect, useRef} from 'react';
import {Box, Typography} from '@mui/material';
import {
  delivered as delShip,  placed, rto as rtoShip,
  cancelled as cancelledShip, transit, deliveryFailed
} from '../../../helpers/UtilityHelper';
import {FONT, WEIGHT} from '../../../styles/foundation/typography';
import {BORDER, SUBTLE, WHITE, PRIMARY_BLUE, STATUS_COLORS} from '../../../styles/foundation/colors';
import {SHADOW} from '../../../styles/foundation/effects';

// ── Animated counter (same as HomePage) ──
function useCountUp(end, duration = 1200) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * end));
      if (p < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [end, duration]);
  return count;
}

// Fixed semantic status palette — same colour as the overview donut / KPI row.
const STATS_FORWARD = [
  {key: 'total', label: 'Total', color: PRIMARY_BLUE},
  {key: 'pending', label: 'Pending', color: STATUS_COLORS.ORDER_PLACED},
  {key: 'inTransit', label: 'In Transit', color: STATUS_COLORS.INTRANSIT},
  {key: 'delivered', label: 'Delivered', color: STATUS_COLORS.DELIVERED},
  {key: 'ndr', label: 'NDR', color: STATUS_COLORS.DELIVERY_FAILED},
  {key: 'rto', label: 'RTO', color: STATUS_COLORS.RTO},
  {key: 'cancelled', label: 'Cancelled', color: STATUS_COLORS.CANCELLED},
];
const STATS_REVERSE = [
  {key: 'total', label: 'Total', color: PRIMARY_BLUE},
  {key: 'pending', label: 'Pending', color: STATUS_COLORS.ORDER_PLACED},
  {key: 'inTransit', label: 'In Transit', color: STATUS_COLORS.INTRANSIT},
  {key: 'delivered', label: 'Delivered', color: STATUS_COLORS.DELIVERED},
  {key: 'npr', label: 'NPR', color: STATUS_COLORS.DELIVERY_FAILED},
  {key: 'cancelled', label: 'Cancelled', color: STATUS_COLORS.CANCELLED},
];

function AnimatedStat({value, color}) {
  const animated = useCountUp(value);
  return (
    <Typography sx={{
      fontSize: '22px', fontWeight: WEIGHT.extra, lineHeight: 1, color, fontFamily: FONT,
      fontVariantNumeric: 'tabular-nums', mt: 0.75,
    }}>
      {animated.toLocaleString()}
    </Typography>
  );
}

const OrderSummary = ({data, flow = 'ALL'}) => {
  const flatData = data || [];
  const isReverse = flow === 'REVERSE';
  const statsConfig = isReverse ? STATS_REVERSE : STATS_FORWARD;
  const counts = {
    total: flatData.length,
    pending: flatData.filter((o) => placed.includes(o.orderStatus)).length,
    inTransit: flatData.filter((o) => transit.includes(o.orderStatus)).length,
    delivered: flatData.filter((o) => delShip.includes(o.orderStatus)).length,
    ndr: flatData.filter((o) => deliveryFailed.includes(o.orderStatus)).length,
    npr: flatData.filter((o) => o.orderStatus === 'PICKUP_FAILED').length,
    rto: flatData.filter((o) => rtoShip.includes(o.orderStatus)).length,
    cancelled: flatData.filter((o) => cancelledShip.includes(o.orderStatus)).length,
  };

  return (
    <>
      {/* Data Cockpit — dense bordered metric strip with tabular numerals */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: {xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)', md: isReverse ? 'repeat(6,1fr)' : 'repeat(7,1fr)'},
        bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden',
        boxShadow: SHADOW.card, mb: 2.5,
      }}>
        {statsConfig.map(({key, label, color}) => (
          <Box key={key} sx={{p: 1.75, borderRight: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
              <Box sx={{width: 7, height: 7, borderRadius: '2px', bgcolor: color, flex: 'none'}} />
              <Typography sx={{fontSize: '9.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.05em', textTransform: 'uppercase', color: SUBTLE, fontFamily: FONT}}>{label}</Typography>
            </Box>
            <AnimatedStat value={counts[key]} color={color} />
          </Box>
        ))}
      </Box>
    </>
  );
};

export default OrderSummary;
